import { useMemo } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  ComposedChart, Bar, Line, Area, XAxis, YAxis, CartesianGrid, Tooltip, Legend,
  ResponsiveContainer, ReferenceLine,
} from "recharts";
import type { StandardLoanAmortizationRow, PolicyLoanLedgerRow } from "@/lib/calculationEngine";
import { buildBehavioralCurve } from "@/lib/chartCurveBuilder";
import type {
  ChartTooltipEntry,
  ChartTooltipRenderProps,
} from "@/lib/chartTypes";
import { entryKey, toNumber, numericField } from "@/lib/chartTypes";

interface BalanceChartProps {
  policyLedger: PolicyLoanLedgerRow[];
  amortization: StandardLoanAmortizationRow[];
}

type TooltipEntry = ChartTooltipEntry;

/* Custom tooltip */
type CustomTooltipProps = ChartTooltipRenderProps & { isDark: boolean };
function CustomTooltip({ active, payload, label, isDark }: CustomTooltipProps) {
  if (!active || !payload?.length) return null;
  const seen = new Set<string>();
  const items: readonly TooltipEntry[] = payload.filter((p) => {
    const key = entryKey(p);
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
  return (
    <div
      className="rounded-xl border px-4 py-3 text-xs shadow-2xl backdrop-blur-sm"
      style={{
        backgroundColor: isDark ? 'hsla(222, 22%, 12%, 0.92)' : 'hsla(0, 0%, 100%, 0.95)',
        borderColor: isDark ? 'hsl(222, 15%, 22%)' : 'hsl(220, 13%, 88%)',
        color: isDark ? 'hsl(220, 14%, 90%)' : 'hsl(220, 26%, 14%)',
      }}
    >
      <p className="mb-2 font-semibold" style={{ color: isDark ? 'hsl(220,14%,80%)' : 'hsl(220,26%,30%)' }}>
        Month {label}
      </p>
      {items.map((entry, idx) => (
        <div key={idx} className="flex items-center justify-between gap-4 py-0.5">
          <span className="flex items-center gap-1.5">
            <span className="inline-block h-2.5 w-2.5 rounded-full" style={{ background: entry.color, boxShadow: `0 0 4px ${entry.color}` }} />
            {String(entry.name ?? '')}
          </span>
          <span className="font-mono font-semibold tabular-nums">
            ${toNumber(entry.value).toLocaleString('en-US', { minimumFractionDigits: 2 })}
          </span>
        </div>
      ))}
    </div>
  );
}

export function BalanceChart({ policyLedger, amortization }: BalanceChartProps) {
  const chartData = useMemo(() => {
    const curve = buildBehavioralCurve(policyLedger, amortization);
    return curve.map(p => ({
      label: p.period,
      policyBalance: p.policyBalance ?? 0,
      standardBalance: p.standardBalance ?? 0,
      marginRecaptureBalance: p.marginRecaptureBalance,
    }));
  }, [policyLedger, amortization]);

  if (chartData.length === 0) {
    return (
      <Card>
        <CardHeader><CardTitle className="text-base">Balance Comparison</CardTitle></CardHeader>
        <CardContent><p className="text-sm text-muted-foreground">Enter loan details to see the comparison chart.</p></CardContent>
      </Card>
    );
  }

  const isDark = typeof window !== 'undefined' && document.documentElement.classList.contains('dark');

  const policyColor = isDark ? "hsl(0, 85%, 62%)" : "hsl(0, 72%, 50%)";
  const policyColorLight = isDark ? "hsl(0, 90%, 72%)" : "hsl(0, 80%, 60%)";
  const standardColor = isDark ? "hsl(210, 90%, 62%)" : "hsl(215, 75%, 48%)";
  const standardColorLight = isDark ? "hsl(210, 95%, 72%)" : "hsl(215, 85%, 60%)";
  const recaptureColor = isDark ? "hsl(142, 70%, 55%)" : "hsl(142, 68%, 42%)";
  const gridColor = isDark ? "hsl(222, 15%, 20%)" : "hsl(220, 13%, 92%)";
  const tickColor = isDark ? "hsl(220, 9%, 55%)" : "hsl(220, 9%, 46%)";

  const showBars = chartData.length <= 60;

  const tickInterval = chartData.length > 48 ? Math.ceil(chartData.length / 12) - 1
    : chartData.length > 24 ? 2 : 0;

  // Month at which the policy loan first reaches $0 — a key milestone.
  let policyPayoffMonth: number | null = null;
  for (let i = 1; i < chartData.length; i++) {
    if (chartData[i - 1].policyBalance > 0.01 && chartData[i].policyBalance <= 0.01) {
      policyPayoffMonth = chartData[i].label;
      break;
    }
  }

  // Fixed legend built from the chart's own colour values. Recharts' auto
  // legend is unreliable here because the Area/Bar entries carry gradient
  // url(#...) fills, which dedupe against the Line entries and can leave the
  // Intended/Policy dots blank. Every rendered series always gets a swatch
  // in its exact colour.
  const legendItems: { key: string; color: string; dashed?: boolean }[] = [];
  if (chartData.some(d => numericField(d, "standardBalance") > 0)) legendItems.push({ key: "Intended Loan", color: standardColor });
  if (chartData.some(d => numericField(d, "policyBalance") > 0)) legendItems.push({ key: "Policy Loan", color: policyColor });
  if (chartData.some(d => numericField(d, "marginRecaptureBalance") > 0)) legendItems.push({ key: "Family Financing Margin™ Growth", color: recaptureColor, dashed: true });

  const fixedLegend = () => (
    <div className="flex flex-wrap justify-center gap-x-4 gap-y-1 pt-2 text-xs">
      {legendItems.map(item => (
        <span key={item.key} className="inline-flex items-center gap-1.5">
          {item.dashed ? (
            <svg width="18" height="4" aria-hidden="true">
              <line x1="0" y1="2" x2="18" y2="2" stroke={item.color} strokeWidth="2.5" strokeDasharray="4 3" />
            </svg>
          ) : (
            <span className="inline-block h-2.5 w-2.5 rounded-full" style={{ backgroundColor: item.color }} />
          )}
          <span className="text-muted-foreground">{item.key}</span>
        </span>
      ))}
    </div>
  );

  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="text-base font-semibold">Estimated Remaining Loan Balance Over Time</CardTitle>
        <p className="text-xs text-muted-foreground mt-0.5">Monthly estimated balance comparison • Policy Loan vs Intended Loan</p>
      </CardHeader>
      <CardContent>
        <ResponsiveContainer width="100%" height={420}>
          <ComposedChart data={chartData} margin={{ top: 10, right: 24, left: 14, bottom: 40 }}>
            <defs>
              <linearGradient id="policyAreaGrad" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor={policyColor} stopOpacity={0.45} />
                <stop offset="40%" stopColor={policyColor} stopOpacity={0.18} />
                <stop offset="100%" stopColor={policyColor} stopOpacity={0.02} />
              </linearGradient>
              <linearGradient id="standardAreaGrad" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor={standardColor} stopOpacity={0.4} />
                <stop offset="40%" stopColor={standardColor} stopOpacity={0.15} />
                <stop offset="100%" stopColor={standardColor} stopOpacity={0.02} />
              </linearGradient>
              <linearGradient id="recaptureAreaGrad" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor={recaptureColor} stopOpacity={0.05} />
                <stop offset="60%" stopColor={recaptureColor} stopOpacity={0.28} />
                <stop offset="100%" stopColor={recaptureColor} stopOpacity={0.5} />
              </linearGradient>
              <linearGradient id="barPolicyGrad3d" x1="0" y1="0" x2="0.3" y2="1">
                <stop offset="0%" stopColor={policyColorLight} stopOpacity={0.85} />
                <stop offset="35%" stopColor={policyColor} stopOpacity={0.7} />
                <stop offset="100%" stopColor={policyColor} stopOpacity={0.35} />
              </linearGradient>
              <linearGradient id="barStandardGrad3d" x1="0" y1="0" x2="0.3" y2="1">
                <stop offset="0%" stopColor={standardColorLight} stopOpacity={0.85} />
                <stop offset="35%" stopColor={standardColor} stopOpacity={0.7} />
                <stop offset="100%" stopColor={standardColor} stopOpacity={0.35} />
              </linearGradient>
              <filter id="glowPolicy" x="-20%" y="-20%" width="140%" height="140%">
                <feGaussianBlur stdDeviation="4" result="blur" />
                <feFlood floodColor={policyColor} floodOpacity="0.5" result="color" />
                <feComposite in="color" in2="blur" operator="in" result="shadow" />
                <feMerge><feMergeNode in="shadow" /><feMergeNode in="SourceGraphic" /></feMerge>
              </filter>
              <filter id="glowStandard" x="-20%" y="-20%" width="140%" height="140%">
                <feGaussianBlur stdDeviation="4" result="blur" />
                <feFlood floodColor={standardColor} floodOpacity="0.45" result="color" />
                <feComposite in="color" in2="blur" operator="in" result="shadow" />
                <feMerge><feMergeNode in="shadow" /><feMergeNode in="SourceGraphic" /></feMerge>
              </filter>
              <filter id="barShadow" x="-10%" y="-5%" width="120%" height="115%">
                <feDropShadow dx="1" dy="2" stdDeviation="2" floodColor="hsl(0,0%,0%)" floodOpacity={isDark ? "0.5" : "0.15"} />
              </filter>
            </defs>

            <CartesianGrid strokeDasharray="3 6" stroke={gridColor} vertical={false} />
            <XAxis
              dataKey="label"
              tick={{ fontSize: 11, fill: tickColor, fontWeight: 500 }}
              interval={tickInterval}
              axisLine={{ stroke: gridColor }}
              tickLine={{ stroke: gridColor }}
              label={{
                value: "Months",
                position: "insideBottom",
                offset: -8,
                style: { fontSize: 12, fill: tickColor, fontWeight: 600, letterSpacing: '0.05em' },
              }}
            />
            <YAxis
              tickFormatter={v => `$${(v / 1000).toFixed(0)}k`}
              tick={{ fontSize: 11, fill: tickColor, fontWeight: 500 }}
              axisLine={false}
              tickLine={false}
            />
            <Tooltip content={<CustomTooltip isDark={isDark} />} />
            <Legend content={fixedLegend} />

            {policyPayoffMonth != null && (
              <ReferenceLine
                x={policyPayoffMonth}
                stroke={recaptureColor}
                strokeDasharray="4 4"
                strokeWidth={1.5}
                label={{
                  value: `Policy Payoff • Mo ${policyPayoffMonth}`,
                  position: "insideTopRight",
                  fill: recaptureColor,
                  fontSize: 10,
                  fontWeight: 600,
                }}
              />
            )}

            <Area type="monotone" dataKey="standardBalance" name="Intended Loan" fill="url(#standardAreaGrad)" stroke="none" legendType="none" animationDuration={1200} />
            <Area type="monotone" dataKey="policyBalance" name="Policy Loan" fill="url(#policyAreaGrad)" stroke="none" legendType="none" animationDuration={1200} />
            <Area type="monotone" dataKey="marginRecaptureBalance" name="Family Financing Margin™ Growth" fill="url(#recaptureAreaGrad)" stroke={recaptureColor} strokeWidth={2.5} strokeDasharray="5 3" connectNulls={false} animationDuration={1400} />

            {showBars && (
              <>
                <Bar dataKey="standardBalance" name="Intended Loan" fill="url(#barStandardGrad3d)" radius={[3, 3, 0, 0]} barSize={chartData.length > 30 ? 5 : 10} filter="url(#barShadow)" animationDuration={1400} />
                <Bar dataKey="policyBalance" name="Policy Loan" fill="url(#barPolicyGrad3d)" radius={[3, 3, 0, 0]} barSize={chartData.length > 30 ? 5 : 10} filter="url(#barShadow)" animationDuration={1400} />
              </>
            )}

            <Line type="monotone" dataKey="standardBalance" name="Intended Loan" stroke={standardColor} strokeWidth={3} dot={false} filter="url(#glowStandard)" animationDuration={1600} />
            <Line type="monotone" dataKey="policyBalance" name="Policy Loan" stroke={policyColor} strokeWidth={3.5} dot={false} filter="url(#glowPolicy)" animationDuration={1600} />
          </ComposedChart>
        </ResponsiveContainer>
      </CardContent>
    </Card>
  );
}
